import { useMemo } from "react";

import {
	SiteControllerType,
	type RoomPriceRank_RoomPriceRankPageQuery,
	type RoomPriceRankDailyStatus_RoomPriceRankPageQuery,
} from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";
import { FeatureIslandHeader } from "@/components/features/_layouts/island/header";

import { Button } from "@/components/ui/button/button";

import { RoomPriceRankCalendar } from "./price-rank-calendar/root";
import { RoomPriceRankList } from "./price-rank-list/root";
import { RoomPriceRankSupplementaryInfo } from "./site-controller-info";
import { useInit } from "../../model/price-rank-and-daily-status/use-case/use-init";

// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	hotelID: string;
	roomPriceRank: RoomPriceRank_RoomPriceRankPageQuery;
	roomPriceRankDailyStatus: RoomPriceRankDailyStatus_RoomPriceRankPageQuery;
	onRefetch: (props: {
		hotelID: string;
		startDt: string;
		endDt: string;
	}) => Promise<ApolloQueryResult<RoomPriceRankDailyStatus_RoomPriceRankPageQuery | undefined>>;
};

export const RoomPriceRankSettingPresenter = ({
	hotelID,
	roomPriceRank: _roomPriceRank,
	roomPriceRankDailyStatus,
	onRefetch,
}: Props) => {
	const {
		onUpdate,
		isDirty,
		isValid,

		roomPriceRank,
		onSetRoomPriceRank,
		fetchNewPriceRankItem,

		dailyStatus,
		onSetDailyStatus,
		refetchRoomPriceRankDailyStatus,

		selectedPriceRank,
		onSetSelectedPriceRank,

		siteControllerSetting,
	} = useInit({ hotelID, roomPriceRank: _roomPriceRank, roomPriceRankDailyStatus, onRefetch });

	const { siteControllerType, setUpRoomPriceByChillnn } = siteControllerSetting;

	const canShowPriceRankSetting = useMemo(() => {
		if (siteControllerType === SiteControllerType.NotUsing) {
			// サイコンを使用していない場合（CHILLNNで料金と在庫を管理している場合）
			return true;
		}

		if (setUpRoomPriceByChillnn) {
			// サイコンを使用しているが、CHILLNNで料金を管理している場合
			return true;
		}

		return false;
	}, [setUpRoomPriceByChillnn, siteControllerType]);

	useTransitionBlock(isDirty);

	return (
		<div>
			<FeatureIslandContainer>
				<FeatureIslandHeader
					title="部屋料金設定"
					rightChildren={
						<Button type="button" size="xl" onClick={onUpdate} variant={isValid ? "default" : "disabled"}>
							保存
						</Button>
					}
				/>

				<FeatureIslandMainWithBackGround>
					<RoomPriceRankSupplementaryInfo siteControllerSetting={siteControllerSetting} />

					{canShowPriceRankSetting && (
						<>
							<div className="space-y-2">
								<p>「選択中」の料金ランクの部屋料金が、カレンダーの各日付に設定されます。</p>
								<p>クリックしながら日付の上を通過することで、部屋の料金を日付に設定できます。</p>
								<p className="text-brand">
									※ このページでの変更は右上の保存ボタンを押すことで保存できます。
								</p>
							</div>

							<div className="flex flex-col gap-y-12 laptop:flex-row laptop:flex-wrap laptop:gap-x-4">
								{/* 料金ランク設定 */}
								<RoomPriceRankList
									fetchNewPriceRankItem={fetchNewPriceRankItem}
									roomPriceRankMast={roomPriceRank}
									onSetRoomPriceRankMast={onSetRoomPriceRank}
									selectedPriceRank={selectedPriceRank}
									onSetSelectedPriceRank={onSetSelectedPriceRank}
								/>
								{/* 料金ランクカレンダー */}
								<RoomPriceRankCalendar
									dailyStatus={dailyStatus}
									onSetDailyStatus={onSetDailyStatus}
									refetchRoomPriceRankDailyStatus={refetchRoomPriceRankDailyStatus}
									selectedPriceRank={selectedPriceRank}
									isDirty={isDirty}
								/>
							</div>
						</>
					)}
				</FeatureIslandMainWithBackGround>
			</FeatureIslandContainer>
		</div>
	);
};

RoomPriceRankSettingPresenter.displayName = "RoomPriceRankSettingPresenter";
